import type { NumberedLink } from '../../utils/markdownLinks';
import type { Source, SourceKind, WebSource } from './sourcePanel.types';

/**
 * Which Statista product a citation came from, read from the parenthetical the report writes
 * into the link label: "Statista Consumer Insights (survey)", "Statista Market Insights
 * (model-based)", "Statista (measured)".
 */
const STATISTA_KIND: Partial<Record<string, Exclude<SourceKind, 'web'>>> = {
  survey: 'survey',
  'model-based': 'forecast',
  measured: 'statistic',
};

const STATISTA_LABEL = /^Statista\b.*\(([^)]+)\)\s*$/i;

/**
 * Builds the record the panel describes. A citation link carries only its label, URL and
 * domain, so a Statista record is recognised by its label and everything else is web research.
 */
export const sourceFromLink = (link: NumberedLink): Source => {
  const kind = STATISTA_KIND[link.label.match(STATISTA_LABEL)?.[1]?.toLowerCase() ?? ''];
  if (!kind) return webSourceFromLink(link);

  return { kind, title: link.label, url: link.url, rows: [] };
};

export const webSourceFromLink = (link: NumberedLink): WebSource => ({
  kind: 'web',
  site: publicationName(link.domain),
  domain: link.domain,
  title: link.label,
  url: link.url,
});

const COMPOUND_SUFFIX = /\.(co|com|org|net|gov|ac|edu)\.[a-z]{2}$/i;

/**
 * Temporary: a hostname cannot give a real masthead, so this guesses one from the registrable
 * label. MFINS-4820 replaces it with the unfurled `og:site_name`.
 */
const publicationName = (domain: string) => {
  const stripped = domain.replace(COMPOUND_SUFFIX, '');
  const labels = stripped.split('.');
  const name = stripped === domain ? (labels[labels.length - 2] ?? labels[0]) : labels.at(-1);
  if (!name) return domain;
  return name
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
